import { GCInstant } from 'src/lib/gcinstant';
import { Feature, type FeatureAnalytics } from 'src/replicant/analytics';
import StateObserver, { user } from 'src/StateObserver';
import { track } from 'src/lib/analytics/utils';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import {
  canAutoAddFriend,
  deriveFriendAddAttribution,
  getBuruFriendsCount,
  isBuruFriend,
} from 'src/replicant/friends/friends.getters';
import i18n from 'src/lib/i18n/i18n';
import features from 'src/replicant/ruleset/features';
import { getVisitDetails } from '../visit/visit.sequence';
import { GENERIC_AVATAR } from 'src/replicant/profile/assets';
import { generateDirectMiniAppURL, generateInviteURL } from 'src/lib/sharing';
import { withLoading } from 'src/lib/utils';
import { getRandomAddFriendsV2ShareCreative } from 'src/creatives/share';
import { getCreativeText } from 'src/creatives/text';
import { openProfile } from '../profile/profile.sequence';
import { puzzleCoinsIsPlayingTutorial } from 'src/replicant/puzzlecoins/puzzlecoins.getters';
import { refreshExternalFriends } from '../social/social.utils';

type AddFriendsV2SubFeature =
  | typeof Feature.FRIENDS.QRCODE_SHARE
  | typeof Feature.FRIENDS.LINE_SHARE
  | typeof Feature.FRIENDS.NATIVE_SHARE
  | typeof Feature.INVITE.LINE_SHARE
  | typeof Feature.INVITE.NATIVE_SHARE
  | typeof Feature.PET_DIALOG.LINE_SHARE
  | typeof Feature.PET_DIALOG.NATIVE_SHARE
  | typeof Feature.PET_DIALOG.QRCODE_SHARE
  | typeof Feature.FRIEND_LIST.LINE_SHARE
  | typeof Feature.FRIEND_LIST.NATIVE_SHARE
  // ─── Legacy ───
  // In-flight links from older clients still carry these old subFeature
  // strings — kept here so they continue to satisfy the v2 type.
  | typeof Feature.FRIENDS.FIND_QRCODE
  | typeof Feature.FRIENDS.FIND_LINESHARE
  | typeof Feature.FRIENDS.FIND_URLSHARE
  | typeof Feature.INVITE.INVITE_ADD_FRIENDS_V2
  | typeof Feature.FRIEND_LIST.LEGACY_LINE_SHARE
  | typeof Feature.FRIEND_LIST.LEGACY_NATIVE_SHARE;

/**
 * Read the subFeature from entry data. Checks both `subFeature` and `$subFeature`
 * field names since payloads may contain either depending on the entry point source.
 */
function getEntrySubFeature<F extends AddFriendsV2SubFeature | undefined>(
  fallback: F,
): AddFriendsV2SubFeature | F {
  return (GCInstant.entryData?.subFeature ??
    GCInstant.entryData?.$subFeature ??
    fallback) as AddFriendsV2SubFeature | F;
}

/**
 * Build a valid FeatureAnalytics object from the entry subFeature.
 * Invite share subFeatures are paired with the INVITE feature namespace;
 * all other v2 subFeatures belong to FRIENDS. Legacy `invite_add_friends_v2`
 * payloads from old in-flight links are also routed to INVITE for compatibility.
 */
function getEntryAnalytics(): FeatureAnalytics {
  const subFeature = getEntrySubFeature(Feature.FRIENDS.QRCODE_SHARE);
  if (
    subFeature === Feature.INVITE.LINE_SHARE ||
    subFeature === Feature.INVITE.NATIVE_SHARE ||
    subFeature === Feature.INVITE.INVITE_ADD_FRIENDS_V2
  ) {
    return { feature: Feature.INVITE._, subFeature };
  }
  if (
    subFeature === Feature.PET_DIALOG.LINE_SHARE ||
    subFeature === Feature.PET_DIALOG.NATIVE_SHARE ||
    subFeature === Feature.PET_DIALOG.QRCODE_SHARE
  ) {
    return { feature: Feature.PET_DIALOG._, subFeature };
  }
  if (
    subFeature === Feature.FRIEND_LIST.LINE_SHARE ||
    subFeature === Feature.FRIEND_LIST.NATIVE_SHARE ||
    subFeature === Feature.FRIEND_LIST.LEGACY_LINE_SHARE ||
    subFeature === Feature.FRIEND_LIST.LEGACY_NATIVE_SHARE
  ) {
    return { feature: Feature.FRIEND_LIST._, subFeature };
  }
  return { feature: Feature.FRIENDS._, subFeature };
}

/**
 * Check if user is entering the game via an Add Friends V2 invite link.
 */
export function isEnteringFromInviteV2(): boolean {
  // Try to find the subFeature in entry data
  const subFeature = getEntrySubFeature(undefined);

  // All Add Friends V2 subFeatures.
  const addFriendsV2SubFeatures: string[] = [
    Feature.INVITE.LINE_SHARE,
    Feature.INVITE.NATIVE_SHARE,
    Feature.FRIENDS.LINE_SHARE,
    Feature.FRIENDS.NATIVE_SHARE,
    Feature.FRIENDS.QRCODE_SHARE,
    Feature.PET_DIALOG.LINE_SHARE,
    Feature.PET_DIALOG.NATIVE_SHARE,
    Feature.PET_DIALOG.QRCODE_SHARE,
    Feature.FRIEND_LIST.LINE_SHARE,
    Feature.FRIEND_LIST.NATIVE_SHARE,
    // ─── Legacy ───
    // In-flight links from older clients still carry these old subFeature
    // strings — kept here so they continue to route to the v2 flow.
    Feature.INVITE.INVITE_ADD_FRIENDS_V2,
    Feature.FRIENDS.FIND_LINESHARE,
    Feature.FRIENDS.FIND_URLSHARE,
    Feature.FRIENDS.FIND_QRCODE,
    Feature.FRIEND_LIST.LEGACY_LINE_SHARE,
    Feature.FRIEND_LIST.LEGACY_NATIVE_SHARE,
  ];

  // Return true if subFeature matches any Add Friends V2 variant
  return subFeature != null && addFriendsV2SubFeatures.includes(subFeature);
}

/**
 * Resolve the inviter this entry is about to befriend, if any.
 *
 * The Add Friends V2 launch sequence establishes the friendship well after
 * `EntryFinal` is sent, so friend analytics would otherwise miss the inviter —
 * the only friend most invited players have on their first entry.
 * Existing recipient-side friends are also returned so a deleted reciprocal
 * state can be restored through the same confirmation flow.
 *
 * @returns The inviter's player ID, or `undefined` when this is not an Add
 * Friends V2 invite entry or the friendship cannot be established.
 */
export function getPendingInviteFriendId(): string | undefined {
  if (!isEnteringFromInviteV2()) {
    return undefined;
  }

  // Web share links carry the inviter in `webReferrer`, regular invites in `playerID`.
  const inviterId =
    GCInstant.entryData?.webReferrer ?? GCInstant.entryData?.playerID;

  if (!inviterId) {
    return undefined;
  }

  const userState = user();
  const recipientAlreadyFriend = isBuruFriend(userState, inviterId);

  // Existing friends may still need their deleted reciprocal state restored.
  if (!recipientAlreadyFriend && !canAutoAddFriend(userState, inviterId)) {
    return undefined;
  }

  return inviterId;
}

/**
 * Resolve the inviter this entry will befriend without asking the player first.
 *
 * `startLaunchSequence` runs `puzzleCoinsLaunchSequence` while the tutorial is
 * incomplete, which adds the inviter automatically. Otherwise
 * `addFriendsV2LaunchSequence` opens a confirmation popup that the player can
 * decline, so only the automatic path is certain enough to count towards friend
 * analytics before the friendship exists.
 *
 * @returns The inviter's player ID, or `undefined` when the friendship is not
 * certain to be established.
 */
export function getAutoAddInviteFriendId(): string | undefined {
  return puzzleCoinsIsPlayingTutorial(user())
    ? getPendingInviteFriendId()
    : undefined;
}

/**
 * Launch sequence for Add Friends V2 invite flow.
 * This sequence is triggered when a user enters the game via an Add Friends V2 invite link.
 */
export async function addFriendsV2LaunchSequence() {
  // Only if user is entering from an Add Friends V2 invite whose inviter can
  // still be befriended
  const inviterId = getPendingInviteFriendId();
  if (!inviterId) return;

  // Receiver is processing a valid incoming invite
  const { originFeature, originSubFeature, method } =
    deriveFriendAddAttribution(GCInstant.entryData);
  track('FriendRequestReceive', {
    senderID: inviterId,
    friendCount: getBuruFriendsCount(user()),
    method,
    originFeature,
    originSubFeature,
    friendRequestSenderID: inviterId,
  });

  // Fetch inviter's profile details for the confirmation popup
  const inviterDetails = await withLoading(
    () => getVisitDetails(inviterId).get(),
    getEntryAnalytics(),
  );

  // If inviter details couldn't be fetched, bail out
  if (!inviterDetails) {
    return;
  }

  track('FriendConfirmPopupOpen', {
    origin: 'launch',
    originFeature,
    originSubFeature,
    friendRequestSenderID: inviterId,
  });

  // Show friend request confirmation popup with inviter's info
  const result = await openPopupPromise('popupFriendRequestConfirm', {
    player: {
      name: inviterDetails.name ?? 'Unknown',
      photo: inviterDetails.photo ?? GENERIC_AVATAR,
      frame: inviterDetails.frame,
      species: inviterDetails.currentEvolution.species,
    },
  });

  // Track the action taken by the receiver
  track('FriendConfirmAction', {
    action: result,
    prompt: 'accept',
    senderID: inviterId,
    friendCount: getBuruFriendsCount(user()),
    origin: 'launch',
    originFeature,
    originSubFeature,
    friendRequestSenderID: inviterId,
  });

  // Skip if user did not confirm
  if (result !== 'accepted') {
    return;
  }

  // Trigger auto-add friend from invite
  await autoAddFriendFromInvite();
}

/**
 * Reconcile an existing one-sided friendship discovered through a friend share link.
 *
 * @param inviterId Player who shared the friend link.
 * @returns Whether the friendship was already reciprocal or was restored.
 */
async function reconcileFriends(inviterId: string): Promise<boolean> {
  // Remember whether reconciliation must add the recipient's missing side.
  const userState = user();
  const recipientAlreadyFriend = isBuruFriend(userState, inviterId);

  // Ask the backend to compare and restore the reciprocal friendship state.
  const friendsReconciled =
    await StateObserver.invoke.reconcileFriends(inviterId);
  if (!friendsReconciled) {
    return false;
  }

  // Existing recipient-side friendships do not need local refresh or analytics.
  if (recipientAlreadyFriend) {
    return true;
  }

  // Make the restored recipient side visible to GCInstant friend consumers.
  await refreshExternalFriends();

  // Track the restored recipient side with the original share attribution.
  const { originFeature, originSubFeature, method } =
    deriveFriendAddAttribution(GCInstant.entryData);

  track('FriendAdd', {
    friendCount: getBuruFriendsCount(user()),
    method,
    originFeature,
    originSubFeature,
    friendRequestReceiverID: user().id,
    friendRequestSenderID: inviterId,
    isReceiver: true,
    isReconciled: true,
  });

  // The one-sided friendship was successfully handled.
  return true;
}

/**
 * Automatically establish friendship between inviter and recipient for Add Friends V2 invite flow.
 * When recipient lands via invite link, both users become friends immediately without requiring approval.
 *
 * @returns Promise that resolves after the invite is processed.
 */
export async function autoAddFriendFromInvite() {
  // Web share links carry the inviter in `webReferrer`, regular invites in `playerID`.
  const inviterId =
    GCInstant.entryData?.webReferrer ?? GCInstant.entryData?.playerID;
  if (!inviterId) return;

  // Handle deleted reciprocal state outside the normal friend-add action.
  const friendsReconciled = await reconcileFriends(inviterId);
  if (friendsReconciled) {
    return;
  }

  // Check if a new friendship can be added through the normal invite flow.
  if (!canAutoAddFriend(user(), inviterId)) {
    return;
  }

  const { originFeature, originSubFeature, method } =
    deriveFriendAddAttribution(GCInstant.entryData);
  const friendAdded = await StateObserver.invoke.addFriendFromInvite({
    inviterId,
    method,
    originFeature,
    originSubFeature,
  });

  if (!friendAdded) {
    await openPopupPromise('popupError', {
      message: i18n(`error.general.tryAgain`),
    });
    return;
  }

  // Make the new friend visible to UI that reads GCInstant.friends right
  // after this sequence (e.g. the launch gifts popup).
  await refreshExternalFriends();

  // Track analytics event
  track('FriendAdd', {
    friendCount: getBuruFriendsCount(user()),
    method,
    originFeature,
    originSubFeature,
    friendRequestReceiverID: user().id,
    friendRequestSenderID: inviterId,
    isReceiver: true,
  });
}

type AddFriendPopupOrigin = 'launch' | 'hud';

type OpenAddFriendPopupArgs = {
  origin: AddFriendPopupOrigin;
  feature?: typeof Feature.PET_DIALOG._;
};

/**
 * Open Add Friend popup for Add Friends V2 users.
 * Only accessible when Add Friends V2 is available.
 */
export async function openAddFriendPopup(args: OpenAddFriendPopupArgs) {
  // Check if Add Friends V2 is available.
  if (!features(user()).addFriendsV2) {
    return;
  }

  // Ensure user has a public profile before showing QR code
  if (!user().publicProfile) {
    await openProfile(args.origin);
  }

  // When opened with pet-dialog attribution, keep shares in that funnel so the
  // receiver's EntryConversion.sourceFeature matches the direct-share path
  // instead of the generic `friends` flow. FeatureAnalytics keeps each feature paired with a
  // subFeature in its own namespace (`<feature>_<method>`); build it in one
  // branch since reading the two fields off a union widens them into an
  // invalid cross-product pair.
  const isPetDialog = args.feature === Feature.PET_DIALOG._;
  const qrShareAnalytics: FeatureAnalytics = isPetDialog
    ? {
        feature: Feature.PET_DIALOG._,
        subFeature: Feature.PET_DIALOG.QRCODE_SHARE,
      }
    : {
        feature: Feature.FRIENDS._,
        subFeature: Feature.FRIENDS.QRCODE_SHARE,
      };
  const urlShareAnalytics: FeatureAnalytics = isPetDialog
    ? {
        feature: Feature.PET_DIALOG._,
        subFeature: Feature.PET_DIALOG.NATIVE_SHARE,
      }
    : {
        feature: Feature.FRIENDS._,
        subFeature: Feature.FRIENDS.NATIVE_SHARE,
      };

  // Generate direct Mini App URL for QR code (bypasses shortlink for faster scanning)
  // Falls back to inviteUrl in popup if generation fails
  const qrCodeUrl = generateDirectMiniAppURL({
    data: {
      feature: qrShareAnalytics.feature,
      $subFeature: qrShareAnalytics.subFeature,
      originFeature: qrShareAnalytics.feature,
      originSubFeature: qrShareAnalytics.subFeature,
    },
  });

  // Generate invite URL with URL share subfeature (used for native share)
  const urlData = await withLoading(
    async () =>
      generateInviteURL({
        creativeAsset: await getRandomAddFriendsV2ShareCreative(),
        creativeText: getCreativeText('invite'),
        data: {
          feature: urlShareAnalytics.feature,
          $subFeature: urlShareAnalytics.subFeature,
          originFeature: urlShareAnalytics.feature,
          originSubFeature: urlShareAnalytics.subFeature,
        },
      }),
    urlShareAnalytics,
  );

  if (!urlData) {
    await openPopupPromise('popupError', {
      message: i18n('error.general.tryAgain'),
    });
    return;
  }

  track('FriendRequestOpen', {
    origin: args.origin,
    ...(args.feature ? { feature: args.feature } : {}),
  });

  // Open popup with the generated invite URL and origin tracking
  await openPopupPromise('popupAddFriend', {
    inviteUrl: urlData.inviteUrl,
    qrCodeUrl,
    origin: args.origin,
    ...(args.feature ? { friendRequestOriginFeature: args.feature } : {}),
    additionalTracking: {
      originFeature: args.origin,
    },
  });
}
